import type { ActivityCalendar, ActivityLevel } from './activity';

export type SummaryExportData = {
  name: string;
  targetRole: string;
  completedDays: number;
  totalDays: number;
  currentStreak: number;
  longestStreak: number;
  activeDays: number;
  questionAttempts: number;
  calendar: ActivityCalendar;
  generatedOn: string;
};

const activityColors: Record<ActivityLevel, string> = {
  0: '#dfe7e9',
  1: '#a9d1bc',
  2: '#64a883',
  3: '#34775a',
  4: '#174c38'
};

function escapeXml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&apos;');
}

function truncate(value: string, length: number): string {
  const characters = Array.from(value.trim());
  return characters.length <= length ? value.trim() : `${characters.slice(0, length - 1).join('')}…`;
}

function metric(x: number, label: string, value: string): string {
  return `
    <g transform="translate(${x} 0)">
      <text y="241" class="metric-label">${escapeXml(label)}</text>
      <text y="284" class="metric-value">${escapeXml(value)}</text>
    </g>`;
}

export function createSummarySvg(data: SummaryExportData): string {
  const cell = 11;
  const gap = 4;
  const graphX = 104;
  const graphY = 440;
  const calendarDays = data.calendar.weeks.flatMap((week) => week.days);
  const squares = calendarDays.map((day, index) => {
    const week = Math.floor(index / 7);
    const weekday = index % 7;
    const fill = day.future ? '#f1f4f5' : activityColors[day.level];
    return `<rect x="${graphX + week * (cell + gap)}" y="${graphY + weekday * (cell + gap)}" width="${cell}" height="${cell}" rx="2" fill="${fill}"/>`;
  }).join('');

  const monthLabels = data.calendar.months.map((month) => {
    const x = graphX + month.startWeek * (cell + gap);
    return `<text x="${x}" y="420" class="month">${escapeXml(month.label)}</text>`;
  }).join('');

  const completedPercent = data.totalDays > 0
    ? Math.round((data.completedDays / data.totalDays) * 100)
    : 0;
  const displayName = truncate(data.name || 'Frontend learner', 48);
  const displayRole = truncate(data.targetRole || 'Frontend developer', 72);

  return `<svg xmlns="http://www.w3.org/2000/svg" width="1200" height="675" viewBox="0 0 1200 675" role="img" aria-labelledby="title description">
  <title id="title">${escapeXml(displayName)} learning summary</title>
  <desc id="description">${data.completedDays} of ${data.totalDays} curriculum days complete. Current streak ${data.currentStreak} days. Best streak ${data.longestStreak} days.</desc>
  <style>
    text { font-family: Arial, sans-serif; fill: #142a35; }
    .brand-small { font-size: 14px; font-weight: 700; letter-spacing: 2px; fill: #f0c85b; }
    .brand { font-family: Georgia, serif; font-size: 28px; font-weight: 700; fill: #ffffff; }
    .header-note { font-size: 17px; fill: #d9e4e8; }
    .name { font-family: Georgia, serif; font-size: 34px; font-weight: 700; fill: #102936; }
    .role { font-size: 17px; fill: #526771; }
    .metric-label { font-size: 15px; fill: #526771; }
    .metric-value { font-family: Georgia, serif; font-size: 31px; font-weight: 700; fill: #102936; }
    .section { font-family: Georgia, serif; font-size: 24px; font-weight: 700; fill: #102936; }
    .month { font-size: 12px; fill: #526771; }
    .footer { font-size: 13px; fill: #526771; }
  </style>
  <rect width="1200" height="675" fill="#e7eef1"/>
  <rect x="40" y="40" width="1120" height="595" rx="10" fill="#fcfdfd" stroke="#102936" stroke-width="2"/>
  <path d="M40 50a10 10 0 0 1 10-10h1100a10 10 0 0 1 10 10v113H40z" fill="#102936"/>
  <g transform="translate(76 72)">
    <path d="M0 0c14-4 27-2 40 7v54c-13-9-26-11-40-7z" fill="#fcfdfd"/>
    <path d="M80 0C66-4 53-2 40 7v54c13-9 26-11 40-7z" fill="#f0c85b"/>
    <path d="M40 7v54" stroke="#d44c2d" stroke-width="4"/>
  </g>
  <text x="184" y="91" class="brand-small">FRONTEND</text>
  <text x="184" y="124" class="brand">Fieldbook</text>
  <text x="1118" y="105" text-anchor="end" class="header-note">Learning record</text>

  <text x="76" y="198" class="name">${escapeXml(displayName)}</text>
  <text x="76" y="224" class="role">${escapeXml(displayRole)} · ${completedPercent}% complete</text>

  ${metric(76, 'Current streak', `${data.currentStreak} ${data.currentStreak === 1 ? 'day' : 'days'}`)}
  ${metric(336, 'Best streak', `${data.longestStreak} ${data.longestStreak === 1 ? 'day' : 'days'}`)}
  ${metric(596, 'Curriculum', `${data.completedDays} / ${data.totalDays}`)}
  ${metric(856, 'Interview answers', String(data.questionAttempts))}

  <line x1="76" y1="324" x2="1124" y2="324" stroke="#b7c5ca" stroke-width="2"/>
  <text x="76" y="373" class="section">Completion activity</text>
  <text x="1124" y="373" text-anchor="end" class="role">${data.activeDays} active ${data.activeDays === 1 ? 'date' : 'dates'}</text>
  ${monthLabels}
  ${squares}

  <text x="76" y="458" class="month">Mon</text>
  <text x="76" y="488" class="month">Wed</text>
  <text x="76" y="518" class="month">Fri</text>
  <line x1="76" y1="571" x2="1124" y2="571" stroke="#b7c5ca"/>
  <text x="76" y="605" class="footer">Frontend Fieldbook summary</text>
  <text x="1124" y="605" text-anchor="end" class="footer">Exported ${escapeXml(data.generatedOn)}</text>
</svg>
`;
}
